/* eslint-disable import/no-unresolved */
import { CloseOutlined } from '@ant-design/icons';
import { Form, Modal, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { INVALID_QUANTITY_MESSAGE } from 'constants/Global/QuantityValidation.const';
import { PackingTypeSale } from 'enums/packingTypeSale';
import { useProductSaleInfo } from 'hooks/useProductSaleInfo';
import { useShippingFee } from 'hooks/useShippingFee';
import Packing from 'model/Packing';
import ProductOffer from 'model/ProductOffer';
import { ButtonPacking } from 'screens/OfferDetails/components/ButtonPacking';
import { initialPackingSelectedState } from 'store/state/gfpOffer/gfpOfferStore';
import { multiplyMoney } from 'utils/number';
import { calculateMinBySaleType } from 'utils/util';

import { usePurchaseInfoDisplay } from 'hooks/usePurchaseInfoDisplay';
import TrolleyLimitModal from '../TrolleyLimitModal';
import * as S from './BuyModal.styled';

interface PurchaseData {
  purchaseType: PackingTypeSale;
  quantity: number;
  totalPrice: number;
  packingSelected: Packing;
}
interface BuyModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (purchaseData: PurchaseData) => void;
  product: ProductOffer | null;
  variant?: 'buy' | 'addToCart';
  packingSelected?: Packing;
}

export function BuyModal({
  visible,
  onCancel,
  onConfirm,
  product,
  variant = 'buy',
  packingSelected: packingSelectedProp,
}: BuyModalProps) {
  const [form] = Form.useForm();
  const { shippingFee, hasShippingFee } = useShippingFee(
    product || ({} as ProductOffer),
  );

  const [quantityPacking, setQuantityPacking] = useState<number>(0);
  const [showTrolleyModal, setShowTrolleyModal] = useState(false);

  const [packingSelected, setPackingSelected] = useState<Packing>(() => {
    if (packingSelectedProp) return packingSelectedProp;
    if (!product?.packings) return {} as Packing;
    const { packings } = product;
    return (
      packings.find((packing) => packing.type === '1') ??
      packings.find((packing) => packing.code === '800') ??
      packings[0]
    );
  });

  useEffect(() => {
    if (packingSelectedProp) {
      setPackingSelected(packingSelectedProp);
    }
  }, [packingSelectedProp]);

  const [selectedTypeSale, setSelectedTypeSale] = useState<PackingTypeSale>(
    () => {
      if (product?.packagingPrice) return PackingTypeSale.Packing;
      if (product?.layerPrice) return PackingTypeSale.Layer;
      return PackingTypeSale.Trolley;
    },
  );

  const modalConfig = useMemo(() => {
    if (variant === 'addToCart') {
      return {
        icon: <S.ShoppingCartIcon />,
        title: `Adicionar ao carrinho: ${product?.name || ''}`,
        buttonText: 'Adicionar ao carrinho',
        modalityLabel: 'Selecione a modalidade e a quantidade:',
      };
    }

    return {
      icon: <S.DollarIcon />,
      title: `Compra agora: ${product?.name || ''}`,
      buttonText: 'Confirmar compra',
      modalityLabel: 'Selecione a modalidade de compra e a quantidade:',
    };
  }, [variant, product?.name]);

  const saleInfo = useProductSaleInfo(selectedTypeSale, packingSelected);
  const purchaseInfo = usePurchaseInfoDisplay(
    selectedTypeSale,
    packingSelected,
    quantityPacking,
    shippingFee,
  );
  const textInputTypeSale = useMemo(
    () => `${saleInfo.inputLabel}(s)`,
    [saleInfo.inputLabel],
  );

  const voucherInfoMessage = useMemo(() => {
    if (!product?.voucherId || !product?.minimumUnitQuantityVoucher)
      return null;
    const discountType =
      product.reajustmentAmount < 0 ? 'desconto' : 'adicional';
    const type = product.voucherQuantityType;
    const qty = product.minimumUnitQuantityVoucher;
    let unitLabel: string;
    if (type === 'P') unitLabel = qty > 1 ? 'embalagens' : 'embalagem';
    else if (type === 'L') unitLabel = qty > 1 ? 'camadas' : 'camada';
    else unitLabel = qty > 1 ? 'carrinhos' : 'carrinho';
    return `*Para aplicação do ${discountType} a quantidade mínima deve ser de ${qty} ${unitLabel}. O valor ${
      product.reajustmentAmount < 0 ? 'do desconto' : 'adicional'
    } será aplicado sobre o preço do produto`;
  }, [
    product?.voucherId,
    product?.minimumUnitQuantityVoucher,
    product?.reajustmentAmount,
    product?.voucherQuantityType,
  ]);

  const directedOfferInfoMessage = useMemo(() => {
    if (!product?.specialPrice || !product?.minimumQuantity) return null;
    const minQty = product.minimumQuantity;
    return minQty === 1
      ? '*Para aplicação do valor promocional, a compra mínima deverá ser de 1 unidade.'
      : `*Para aplicação do valor promocional, a compra mínima deverá ser de ${minQty} unidades.`;
  }, [product?.specialPrice, product?.minimumQuantity]);

  const getLabel = useCallback(() => {
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return packingSelected?.maxAvailablePackingsLabel || '';
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      return packingSelected?.maxAvailableLayersLabel || '';
    }
    return packingSelected?.maxAvailableTrolleysLabel || '';
  }, [
    selectedTypeSale,
    packingSelected?.maxAvailableTrolleysLabel,
    packingSelected?.maxAvailableLayersLabel,
    packingSelected?.maxAvailablePackingsLabel,
  ]);

  const getMultipleCalc = useCallback(() => {
    if (!packingSelected?.multiple) return 1;
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return (
        packingSelected?.multiple / +packingSelected?.maxAvailablePackingsLabel
      );
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      const values = packingSelected?.maxAvailableLayersLabel
        ?.split('x')
        .map((x) => +x);
      if (!values || values.length === 0) return 1;
      const calc = packingSelected?.multiple / values.reduce((a, b) => +a * +b);
      return calc < 1 ? 1 : calc;
    }
    const values = packingSelected?.maxAvailableTrolleysLabel
      ?.split('x')
      .map((x) => +x);
    if (!values || values.length === 0) return 1;
    const calc = packingSelected?.multiple / values.reduce((a, b) => +a * +b);
    return calc < 1 ? 1 : calc;
  }, [
    selectedTypeSale,
    packingSelected?.multiple,
    packingSelected?.maxAvailableTrolleysLabel,
    packingSelected?.maxAvailableLayersLabel,
    packingSelected?.maxAvailablePackingsLabel,
  ]);

  const getErrorMinMessage = useCallback(() => {
    if (!packingSelected?.minimumQuantity) return '';
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return `Quantidade mínima: ${
        packingSelected?.minimumQuantity /
        +packingSelected?.maxAvailablePackingsLabel
      } ${textInputTypeSale}`;
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      const values = packingSelected?.maxAvailableLayersLabel
        ?.split('x')
        .map((x) => +x);
      if (!values || values.length === 0) return '';
      const calc =
        packingSelected?.minimumQuantity / values.reduce((a, b) => +a * +b);
      return `Quantidade mínima: ${calc < 1 ? 1 : calc} ${textInputTypeSale}`;
    }
    const values = packingSelected?.maxAvailableTrolleysLabel
      ?.split('x')
      .map((x) => +x);
    if (!values || values.length === 0) return '';
    const calc =
      packingSelected?.minimumQuantity / values.reduce((a, b) => +a * +b);
    return `Quantidade mínima: ${calc < 1 ? 1 : calc} ${textInputTypeSale}`;
  }, [
    selectedTypeSale,
    packingSelected?.maxAvailableTrolleysLabel,
    packingSelected?.minimumQuantity,
    packingSelected?.maxAvailablePackingsLabel,
    packingSelected?.maxAvailableLayersLabel,
    textInputTypeSale,
  ]);

  const validateMaximumQuantity = useCallback(
    (_: unknown, value: string) => {
      if (!value) return Promise.resolve();

      const numValue = parseInt(value, 10);

      let maxBySaleType = 0;

      switch (selectedTypeSale) {
        case PackingTypeSale.Layer:
          maxBySaleType = packingSelected?.maxAvailableLayersQuantity || 0;
          break;

        case PackingTypeSale.Trolley:
          maxBySaleType = packingSelected?.maxAvailableTrolleysQuantity || 0;
          break;

        default:
          maxBySaleType = packingSelected?.maxAvailablePackingsQuantity || 0;
      }

      if (numValue > maxBySaleType) {
        return Promise.reject(
          new Error(`Quantidade máxima: ${maxBySaleType} ${textInputTypeSale}`),
        );
      }

      return Promise.resolve();
    },
    [
      selectedTypeSale,
      packingSelected?.maxAvailablePackingsQuantity,
      packingSelected?.maxAvailableLayersQuantity,
      packingSelected?.maxAvailableTrolleysQuantity,
      textInputTypeSale,
    ],
  );

  const validateMinimumQuantity = useCallback(
    (_: unknown, value: string) => {
      if (!value) return Promise.resolve();
      const numValue = parseInt(value, 10);
      const minBySaleType = calculateMinBySaleType(
        selectedTypeSale,
        packingSelected ?? initialPackingSelectedState,
      );
      if (packingSelected?.minimumQuantity && numValue < minBySaleType) {
        return Promise.reject(new Error(getErrorMinMessage()));
      }
      return Promise.resolve();
    },
    [selectedTypeSale, packingSelected, getErrorMinMessage],
  );

  const validateMultiple = useCallback(
    (_: unknown, value: string) => {
      if (!value || !packingSelected?.multiple) return Promise.resolve();
      const numValue = parseInt(value, 10);
      const label = getLabel();
      const multipleCalc = getMultipleCalc();

      if (selectedTypeSale === PackingTypeSale.Packing) {
        const totalUnits = numValue * parseInt(label, 10);
        if (totalUnits % packingSelected.multiple !== 0) {
          return Promise.reject(
            new Error(
              `A quantidade não é múltipla de ${multipleCalc} ${textInputTypeSale}`,
            ),
          );
        }
      } else {
        const values = label.split('x').map((x) => parseInt(x, 10));
        const totalUnits = values.reduce((a, b) => a * b, 1) * numValue;
        if (totalUnits % packingSelected.multiple !== 0) {
          return Promise.reject(
            new Error(
              `A quantidade não é múltipla de ${multipleCalc} ${textInputTypeSale}`,
            ),
          );
        }
      }
      return Promise.resolve();
    },
    [
      selectedTypeSale,
      packingSelected,
      textInputTypeSale,
      getLabel,
      getMultipleCalc,
    ],
  );

  const handleTypeSale = useCallback(
    (packingType: PackingTypeSale) => {
      setSelectedTypeSale(packingType);
      form.setFieldsValue({ quantityPacking: '' });
      setQuantityPacking(0);
    },
    [form],
  );

  const handleQuantityChange = useCallback(
    (value: string) => {
      const numValue = parseInt(value, 10) || 0;
      setQuantityPacking(numValue);
      form.setFieldsValue({ quantityPacking: numValue });
    },
    [form],
  );

  const getCurrentPrice = useCallback(() => {
    if (!product) return 0;
    switch (selectedTypeSale) {
      case PackingTypeSale.Packing:
        return product.packagingPrice || 0;
      case PackingTypeSale.Layer:
        return product.layerPrice || 0;
      case PackingTypeSale.Trolley:
        return product.trolleyPrice || 0;
      default:
        return 0;
    }
  }, [selectedTypeSale, product]);

  const isQuantityValid = useMemo(() => quantityPacking > 0, [quantityPacking]);

  const handleSubmit = () => {
    if (!product || !isQuantityValid) return;
    const shouldConfirmTrolleyLimit =
      variant === 'buy'
        ? purchaseInfo.exceedsTrolley && product.isLkpOffer
        : purchaseInfo.exceedsTrolley;
    if (shouldConfirmTrolleyLimit) {
      setShowTrolleyModal(true);
      return;
    }
    onConfirm({
      purchaseType: selectedTypeSale,
      quantity: quantityPacking,
      totalPrice: multiplyMoney(getCurrentPrice(), purchaseInfo.totalUnits),
      packingSelected,
    });
  };

  const handleTrolleyConfirm = () => {
    setShowTrolleyModal(false);
    if (product) {
      onConfirm({
        purchaseType: selectedTypeSale,
        quantity: quantityPacking,
        totalPrice: multiplyMoney(getCurrentPrice(), purchaseInfo.totalUnits),
        packingSelected,
      });
    }
  };

  if (!product) return null;

  return (
    <Modal
      title={
        <S.ModalHeader>
          <S.TitleContainer>
            {modalConfig.icon}
            <S.ModalTitle>{modalConfig.title}</S.ModalTitle>
          </S.TitleContainer>
          <S.CloseButton onClick={onCancel}>
            <CloseOutlined />
          </S.CloseButton>
        </S.ModalHeader>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={500}
      centered
      closable={false}
    >
      <S.ModalContent>
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <S.ModalitySection>
            <S.ModalityLabel>{modalConfig.modalityLabel}</S.ModalityLabel>
            <S.ModalityButtonsContainer>
              <ButtonPacking
                handleTypeSale={handleTypeSale}
                selectedTypeSale={selectedTypeSale}
                packingSelected={packingSelected}
                price={product.packagingPrice}
                packingCost={product.packingCost}
                packingType={PackingTypeSale.Packing}
                quantityPacking={quantityPacking}
                specialPrice={product.specialPrice}
                minimumQuantity={product.minimumQuantity}
              />
              <ButtonPacking
                handleTypeSale={handleTypeSale}
                selectedTypeSale={selectedTypeSale}
                packingSelected={packingSelected}
                price={product.layerPrice}
                packingCost={product.packingCost}
                packingType={PackingTypeSale.Layer}
                quantityPacking={quantityPacking}
                specialPrice={product.specialPrice}
                minimumQuantity={product.minimumQuantity}
              />
              <ButtonPacking
                handleTypeSale={handleTypeSale}
                selectedTypeSale={selectedTypeSale}
                packingSelected={packingSelected}
                price={product.trolleyPrice}
                packingCost={product.packingCost}
                packingType={PackingTypeSale.Trolley}
                quantityPacking={quantityPacking}
                specialPrice={product.specialPrice}
                minimumQuantity={product.minimumQuantity}
              />
            </S.ModalityButtonsContainer>
          </S.ModalitySection>

          <S.QuantitySection>
            <Form.Item
              name="quantityPacking"
              validateTrigger="onSubmit"
              rules={[
                {
                  required: true,
                  message: 'A quantidade é obrigatória.',
                },
                {
                  validator: validateMaximumQuantity,
                },
                {
                  validator: validateMinimumQuantity,
                },
                {
                  validator: validateMultiple,
                },
              ]}
            >
              <S.MainQuantityInput
                placeholder="Digite aqui"
                addonAfter={textInputTypeSale}
                onChange={(e) => handleQuantityChange(e.target.value)}
                size="large"
              />
            </Form.Item>
          </S.QuantitySection>

          <S.PurchaseInfo>
            <S.PurchaseInfoRow>
              <S.InfoItem>
                <span>{purchaseInfo.qtdMinimaText}</span>
              </S.InfoItem>
              |
              <S.InfoItem>
                <span>Total disponível:</span>
                <span>{saleInfo.max}</span>
              </S.InfoItem>
              |
              <S.ShippingInfo>
                <span>{purchaseInfo.shippingFeeText}</span>
              </S.ShippingInfo>
            </S.PurchaseInfoRow>
            {purchaseInfo.compositionLine && (
              <S.CompositionRow>{purchaseInfo.compositionLine}</S.CompositionRow>
            )}
            {purchaseInfo.totalPedidoText && (
              <S.TotalPedidoRow>{purchaseInfo.totalPedidoText}</S.TotalPedidoRow>
            )}
            {voucherInfoMessage && (
              <S.PromoInfoRow>{voucherInfoMessage}</S.PromoInfoRow>
            )}
            {directedOfferInfoMessage && (
              <S.PromoInfoRow>{directedOfferInfoMessage}</S.PromoInfoRow>
            )}
          </S.PurchaseInfo>

          <S.ActionButtons>
            <S.CancelButton type="button" onClick={onCancel}>
              Cancelar
            </S.CancelButton>
            <Tooltip title={isQuantityValid ? '' : INVALID_QUANTITY_MESSAGE}>
              <S.ButtonTooltipWrapper isDisabled={!isQuantityValid}>
                <S.BuyButton type="submit" disabled={!isQuantityValid}>
                  {modalConfig.buttonText}
                </S.BuyButton>
              </S.ButtonTooltipWrapper>
            </Tooltip>
          </S.ActionButtons>
        </Form>
      </S.ModalContent>

      <TrolleyLimitModal
        visible={showTrolleyModal}
        totalUnits={purchaseInfo.totalUnits}
        quantityOrdered={quantityPacking}
        selectedTypeSale={selectedTypeSale}
        unitsPerType={purchaseInfo.unitsPerType}
        totalPrice={multiplyMoney(getCurrentPrice(), purchaseInfo.totalUnits)}
        shippingFeeTotal={
          hasShippingFee ? purchaseInfo.totalUnits * shippingFee : undefined
        }
        onConfirm={handleTrolleyConfirm}
        onCancel={() => setShowTrolleyModal(false)}
      />
    </Modal>
  );
}
